import Notification from './Notification';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import DoneIcon from '@material-ui/icons/Done';
import WarningIcon from '@material-ui/icons/Warning';
import PropTypes from 'prop-types';
import React from 'react';
import DeleteAdornment from './DeleteAdornment';
import AsteriskIcon from './icons/Asterisk';
import LanguageAdornment from './LanguageAdornment';
import { globalStyles } from './style/gstheme';
import { createLengthWarning2, lightSanitiseText, sanitiseText, isRTL } from './utils/utils';
import ReifyButton from './ReifyButton';

const styles = Object.assign({}, globalStyles);

class LangConfirmationField extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            originalText: props.text,
            text: props.text.trim(),
            isDirty: false
        };
    }

    static getDerivedStateFromProps(props, state) {
        if (props.text !== state.originalText) {
            return {
                text: props.text.trim(),
                originalText: props.text
            };
        }
        return null;
    }

    handleChange = (event) => {
        const t = event.target;
        const cleanText = this.props.multiline ? lightSanitiseText(t.value) : sanitiseText(t.value);
        this.setState({
            isDirty: true,
            text: cleanText
        });
    };

    reset = () => this.setState({
        text: this.state.originalText,
        isDirty: false
    });

    handleUpdate = () => {
        this.setState({ isDirty: false });
        this.props.onUpdate(this.state.text.trim());
    };

    handleDelete = () => this.props.onDelete(this.state.text);

    render() {

        const { classes, isReadOnly, label, lang, canBeDeleted, existingValues, invalidDatatype, dataTest,
            focus, allowEmpty, margin, multiline, placeholder, maxLength, highlightNoDelete, graph,
            reify, onReify, source, property, applicableStatements, ariaLabel } = this.props;
        const { text, originalText, isDirty } = this.state;
        let icon = null;
        const isDuplicate = existingValues.length > 0 && existingValues.includes(text.trim());
        const isInvalid = isDuplicate && text.trim() !== originalText;
        const rtl = isRTL(lang);

        if (text.trim().length > 0 && !isReadOnly && text.trim() !== originalText.trim() && !isDuplicate) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="Confirm change" onClick={this.handleUpdate} className={classes.smallIconButton}>
                        <IconButton aria-label="Done editing" data-test="done">
                            <DoneIcon />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>;
        }
        else if ((text === "" && !allowEmpty) || isInvalid) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="Text is mandatory">
                        <WarningIcon data-test="warning" className={classes.inputIcon} />
                    </Tooltip>
                </InputAdornment>;
        }
        else if (!isReadOnly && canBeDeleted && text === originalText && text !== "") {
            icon = <DeleteAdornment dataTest={dataTest} fontSize="small" onClick={this.handleDelete} />;
        }
        else if (!canBeDeleted && !isReadOnly && highlightNoDelete) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="The default language value cannot be deleted">
                        <AsteriskIcon data-test="no-delete" fontSize="small" className={classes.inputIcon} style={{ marginRight: '5px' }} />
                    </Tooltip>
                </InputAdornment>;
        }

        return (
            <ClickAwayListener onClickAway={this.reset}>
                <div>
                    <div
                        style={{ display: 'flex' }}
                        data-test={dataTest + "-container"}>
                        {reify &&
                            <ReifyButton
                                subject={source}
                                property={property}
                                isReadOnly={isReadOnly}
                                graph={graph}
                                value={{
                                    "@value": text,
                                    "language": lang
                                }}
                                applicableStatements={applicableStatements}
                                onReify={onReify} />
                        }
                        <TextField
                            data-test={dataTest + "-container"}
                            value={text}
                            style={margin ? { marginTop: '6px' } : { marginTop: '0px' }}
                            variant="outlined"
                            margin="dense"
                            onChange={this.handleChange}
                            error={(!allowEmpty && text.trim().length === 0) || text.toString().trim().length > maxLength || isInvalid}
                            label={label}
                            fullWidth
                            multiline={multiline}
                            autoFocus={focus}
                            placeholder={allowEmpty ? placeholder : "You must enter text"}
                            onKeyPress={(ev) => {
                                if (!isReadOnly && !isInvalid && !multiline && text.trim().length > 0 && ev.key === 'Enter') {
                                    this.handleUpdate();
                                    ev.preventDefault();
                                    ev.stopPropagation();
                                }
                            }}
                            helperText={
                                invalidDatatype ?
                                    <span data-test="helper-text">This value is no longer valid for the property's datatype.</span>
                                    :
                                    isDirty ?
                                        (isInvalid ?
                                            <span id="invalid-error" data-test="helper-text">This value already exists.</span>
                                            :
                                            (multiline ?
                                                <span data-test="helper-text">{text.length + " of " + this.props.maxLength + " characters."}</span>
                                                :
                                                createLengthWarning2(text, maxLength, isDirty)
                                            )
                                        )
                                        :
                                        createLengthWarning2(text, maxLength, isDirty)
                            }
                            InputProps={{
                                className: classes.inputLabel,
                                endAdornment: icon,
                                inputProps: {
                                    "aria-label": ariaLabel,
                                    "data-test": dataTest,
                                    ...(rtl) && { dir: "rtl" },
                                    "lang": lang,
                                    ...(isInvalid) && {
                                        "aria-describedby": "invalid-error"
                                    },
                                    readOnly: isReadOnly || invalidDatatype,
                                    maxLength: this.props.maxLength
                                },
                                startAdornment: (<LanguageAdornment lang={lang} />)
                            }
                            } />
                    </div>
                    {!multiline && text.includes("\n") &&
                        <Notification error={false} text="The above value contains newlines in the original value but these are suppressed as this is not a multiline field." dataTest="not-multiline" />
                    }
                </div>
            </ClickAwayListener>
        );
    }
}

LangConfirmationField.defaultProps = {
    isReadOnly: false,
    canBeDeleted: false,
    allowEmpty: false,
    focus: false,
    multiline: false,
    margin: false,
    highlightNoDelete: false,
    invalidDatatype: false,
    existingValues: [],
    maxLength: 1000
};

LangConfirmationField.propTypes = {
    dataTest: PropTypes.string,
    isReadOnly: PropTypes.bool,
    margin: PropTypes.bool,
    onUpdate: PropTypes.func,
    focus: PropTypes.bool,
    onDelete: PropTypes.func,
    ariaLabel: PropTypes.string,
    allowEmpty: PropTypes.bool,
    canBeDeleted: PropTypes.bool,
    highlightNoDelete: PropTypes.bool,
    existingValues: PropTypes.array,
    multiline: PropTypes.bool,
    invalidDatatype: PropTypes.bool,
    maxLength: PropTypes.number,
    placeholder: PropTypes.string,
    lang: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    label: PropTypes.string,
    graph: PropTypes.string,
    reify: PropTypes.bool,
    onReify: PropTypes.func,
    source: PropTypes.string,
    property: PropTypes.string,
    applicableStatements: PropTypes.array,
    classes: PropTypes.object
};

export default withStyles(styles)(LangConfirmationField);